import { z } from 'zod';

export const MemberTierEnum = z.enum(['plus', 'power', 'pro', 'pro2']);
export type MemberTier = z.infer<typeof MemberTierEnum>;

export const BillingIntervalEnum = z.enum(['monthly', 'annual']);
export type BillingInterval = z.infer<typeof BillingIntervalEnum>;

export const TeamRoleEnum = z.enum(['owner', 'admin', 'member']);
export type TeamRole = z.infer<typeof TeamRoleEnum>;

export const MemberStatusEnum = z.enum(['active', 'invited', 'deactivated']);
export type MemberStatus = z.infer<typeof MemberStatusEnum>;

export const EnforcedExcludedAppSchema = z.object({
  bundle_id: z.string(),
  name: z.string(),
  platform: z.enum(['mac', 'windows']),
});
export type EnforcedExcludedApp = z.infer<typeof EnforcedExcludedAppSchema>;

export const ChatModeEnum = z.enum(['lite', 'regular', 'max', 'ultra']);
export type ChatMode = z.infer<typeof ChatModeEnum>;

export type TeamAppList = 'exclusions' | 'inclusions';

export const MeetingRecordingRuleSchema = z.object({
  id: z.string(),
  kind: z.enum(['title_keyword', 'attendee_email', 'attendee_domain']),
  value: z.string(),
  added_by: z.string().nullable(),
});
export type MeetingRecordingRule = z.infer<typeof MeetingRecordingRuleSchema>;
export type MeetingRuleDraft = Pick<MeetingRecordingRule, 'kind' | 'value'>;

export const IntegrationOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  icon_url: z.string().nullable(),
});
export type IntegrationOption = z.infer<typeof IntegrationOptionSchema>;

export const MyTeamSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: TeamRoleEnum,
  tier: MemberTierEnum,
  allowed_domains: z.array(z.string()).optional().default([]),
  baa_enabled: z.boolean().optional().default(false),
  enforced_excluded_apps: EnforcedExcludedAppSchema.array()
    .optional()
    .default([]),
  enforced_excluded_domains: z.array(z.string()).optional().default([]),
  enforced_app_access_mode: z
    .enum(['block', 'allow'])
    .optional()
    .default('block'),
  enforced_allowed_apps: EnforcedExcludedAppSchema.array()
    .optional()
    .default([]),
  store_meeting_transcripts: z.boolean().optional().default(true),
  allowed_chat_modes: ChatModeEnum.array().nullable().optional().default(null),
  allowed_integrations: z.array(z.string()).nullable().optional().default(null),
  integration_options: IntegrationOptionSchema.array().optional().default([]),
  custom_instructions: z.string().optional().default(''),
  meeting_recording_rules: MeetingRecordingRuleSchema.array()
    .optional()
    .default([]),
});
export type MyTeam = z.infer<typeof MyTeamSchema>;

export const DiscoverableTeamSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  member_count: z.number(),
  source: z.enum(['domain', 'invite']),
  invite_token: z.string().nullable().optional(),
});
export type DiscoverableTeam = z.infer<typeof DiscoverableTeamSchema>;

export const TeamPricingEntrySchema = z.object({
  tier: MemberTierEnum,
  interval: BillingIntervalEnum,
  amount_cents: z.number(),
  currency: z.string(),
});

export const TeamPricingSchema = z.object({
  tiers: z.array(z.unknown()).transform((entries) =>
    entries.flatMap((entry) => {
      const parsed = TeamPricingEntrySchema.safeParse(entry);
      return parsed.success ? [parsed.data] : [];
    })
  ),
});
export type TeamPricing = z.infer<typeof TeamPricingSchema>;

export const TeamMemberSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string().nullable(),
  role: TeamRoleEnum,
  status: MemberStatusEnum,
  tier: MemberTierEnum,
  invite_token: z.string().nullable().optional(),
  joined_at: z.string().nullable(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const TeamBillingStatusSchema = z.object({
  seat_count: z.number(),
  plus_seats: z.number(),
  power_seats: z.number(),
  pro_seats: z.number(),
  pro2_seats: z.number(),
  stripe_subscription_status: z.string().nullable(),
  current_period_end: z.number().nullable(),
  cancel_at_period_end: z.boolean(),
  billing_interval: BillingIntervalEnum.nullable(),
  credit_balance_cents: z.number().default(0),
});
export type TeamBillingStatus = z.infer<typeof TeamBillingStatusSchema>;

export const IndividualPlanCreditSchema = z.object({
  has_stripe_subscription: z.boolean(),
  credit_cents: z.number(),
});
export type IndividualPlanCredit = z.infer<typeof IndividualPlanCreditSchema>;

export const TeamDashboardDataSchema = z.object({
  team: MyTeamSchema,
  members: TeamMemberSchema.array(),
  billing: TeamBillingStatusSchema,
});
export type TeamDashboardData = z.infer<typeof TeamDashboardDataSchema>;

export const TeamSsoSchema = z.object({
  status: z.enum(['not_set_up', 'in_progress', 'connected']),
  domains: z.array(z.string()),
  required: z.boolean(),
  enabled: z.boolean(),
});
export type TeamSso = z.infer<typeof TeamSsoSchema>;

export interface CreateTeamRequest {
  name: string;
  billing_interval: BillingInterval;
  owner_tier: MemberTier;
}

export interface InviteMemberRequest {
  email: string;
  tier: MemberTier;
  role: Exclude<TeamRole, 'owner'>;
}

export interface UpdateMemberRoleRequest {
  role: Exclude<TeamRole, 'owner'>;
}

export interface UpdateMemberTierRequest {
  tier: MemberTier;
}

export interface UpdateTeamDataControlsRequest {
  store_meeting_transcripts?: boolean;
  enforced_app_access_mode?: MyTeam['enforced_app_access_mode'];
  allowed_chat_modes?: ChatMode[];
  allowed_integrations?: string[] | null;
  custom_instructions?: string;
}

export const TEAM_CUSTOM_INSTRUCTIONS_MAX_LENGTH = 4000;

export enum TierDisplayLabel {
  plus = 'Plus',
  power = 'Power',
  pro = 'Pro',
  pro2 = 'Pro2',
}

export enum TeamRoleDisplayLabel {
  owner = 'Owner',
  admin = 'Admin',
  member = 'Member',
}

export type TeamTab = 'members' | 'data-controls' | 'billing' | 'analytics';

export const TeamTabTitle: Record<TeamTab, string> = {
  members: 'Members',
  'data-controls': 'Data Controls',
  billing: 'Billing',
  analytics: 'Analytics',
};

export const visibleTeamTabs = (isAdmin: boolean): TeamTab[] =>
  isAdmin
    ? ['members', 'analytics', 'data-controls', 'billing']
    : ['members', 'analytics'];

export function parseTeamTab(
  value: string | undefined,
  isAdmin: boolean
): TeamTab {
  return visibleTeamTabs(isAdmin).find((tab) => tab === value) ?? 'members';
}

export const TeamAutoReloadSettingsSchema = z.object({
  enabled: z.boolean(),
  threshold_value_cents: z.number().nullable().optional(),
  topup_value_cents: z.number().nullable().optional(),
  monthly_cap_cents: z.number().nullable().optional(),
  last_failure_reason: z.string().nullable().optional(),
});
export type TeamAutoReloadSettings = z.infer<
  typeof TeamAutoReloadSettingsSchema
>;

export const TeamWalletResponseSchema = z.object({
  team_wallet: z.object({
    plan_balance: z.number(),
    purchased_balance: z.number(),
    daily_balance: z.number(),
    monthly_spend_cap: z.number().nullable(),
    team_draw_mtd: z.number(),
  }),
  rate_cents_per_1000: z.number().optional(),
  payment_method: z
    .object({ brand: z.string(), last4: z.string() })
    .nullable()
    .optional(),
  auto_reload: TeamAutoReloadSettingsSchema.optional(),
});
export type TeamWalletResponse = z.infer<typeof TeamWalletResponseSchema>;

export const UsageFeatureEnum = z.enum([
  'chat',
  'integrations',
  'meetings',
  'routines',
]);
export type UsageFeature = z.infer<typeof UsageFeatureEnum>;

export const UsageDayCountSchema = z.object({
  date: z.string(),
  count: z.number(),
});
export type UsageDayCount = z.infer<typeof UsageDayCountSchema>;

export const TeamUsageMemberSchema = z.object({
  membership_id: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  role: TeamRoleEnum,
  joined_at: z.string().nullable(),
  messages: z.number(),
  meetings: z.number(),
  routine_runs: z.number(),
  integration_actions: z.number().default(0),
  activity_total: z.number(),
  is_active: z.boolean(),
  messages_by_day: UsageDayCountSchema.array(),
});
export type TeamUsageMember = z.infer<typeof TeamUsageMemberSchema>;

const UsageTotalsSchema = z.object({
  chat_messages: z.number(),
  integration_actions: z.number(),
  routine_runs: z.number(),
  meetings: z.number(),
});
export type TeamUsageTotals = z.infer<typeof UsageTotalsSchema>;

export const TeamUsageResponseSchema = z.object({
  window: z.object({
    days: z.number(),
    start: z.string(),
  }),
  totals: UsageTotalsSchema,
  previous_totals: UsageTotalsSchema.optional(),
  messages_by_day: UsageDayCountSchema.array(),
  feature_adoption: z.array(
    z.object({
      feature: UsageFeatureEnum,
      pct_of_active_members: z.number(),
    })
  ),
  top_integrations: z.array(
    z.object({
      app: z.string(),
      actions: z.number(),
    })
  ),
  members: TeamUsageMemberSchema.array(),
});
export type TeamUsageResponse = z.infer<typeof TeamUsageResponseSchema>;

export interface UpdateTeamAutoReloadRequest {
  enabled: boolean;
  threshold_value_cents?: number | null;
  topup_value_cents?: number | null;
  monthly_cap_cents?: number | null;
}
